import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { PaketTeilleistung } from '../../types/types';

export interface PaketpreisCurrentLeistung {
    name: string;
    einzelpreisBrutto: number;
    dauerMinuten: number;
    materialKostenNetto?: number;
    materialMenge?: number;
    geraeteKostenNetto?: number;
}

export interface PaketpreisSavePayload {
    name: string;
    kategorie: string;
    bedienzeit: number;
    materialEinsatz: number;
    materialMenge?: number;
    geräteEinsatz: number;
    finalerPreis: number;
    preisProStunde: number;
    isPackage: true;
    packageServices: PaketTeilleistung[];
    gesamtDauerMinuten: number;
}

/** Bleibt erhalten, wenn das Modal zu ist — nächste Leistung wird im Kalkulator gerechnet. */
export interface PaketpreisDraft {
    titel: string;
    kategorie: string;
    customKat: string;
    paketpreisRaw: string;
    naechsterName: string;
    services: PaketTeilleistung[];
}

export const emptyPaketpreisDraft = (): PaketpreisDraft => ({
    titel: '',
    kategorie: 'Damen',
    customKat: '',
    paketpreisRaw: '',
    naechsterName: '',
    services: [],
});

export const PAKETPREIS_DRAFT_KEY = 'pe_paketpreis_draft';
export const PAKETPREIS_TITEL_KEY = 'pe_paketpreis_letzter_titel';

export function loadLetzterPaketTitel(): string {
    try {
        return String(sessionStorage.getItem(PAKETPREIS_TITEL_KEY) || '').trim();
    } catch {
        return '';
    }
}

export function rememberPaketTitel(titel: string) {
    const clean = String(titel || '').trim();
    if (clean.length < 2) return;
    try { sessionStorage.setItem(PAKETPREIS_TITEL_KEY, clean); } catch { /* ignore */ }
}

export function loadPaketpreisDraft(): PaketpreisDraft {
    try {
        const raw = sessionStorage.getItem(PAKETPREIS_DRAFT_KEY);
        if (!raw) return emptyPaketpreisDraft();
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return emptyPaketpreisDraft();
        return {
            ...emptyPaketpreisDraft(),
            ...parsed,
            services: Array.isArray(parsed.services) ? parsed.services : [],
        };
    } catch {
        return emptyPaketpreisDraft();
    }
}

export function savePaketpreisDraft(draft: PaketpreisDraft) {
    try {
        const empty = !draft.titel && !draft.paketpreisRaw && !draft.naechsterName && draft.services.length === 0 && draft.kategorie === 'Damen' && !draft.customKat;
        if (empty) sessionStorage.removeItem(PAKETPREIS_DRAFT_KEY);
        else sessionStorage.setItem(PAKETPREIS_DRAFT_KEY, JSON.stringify(draft));
    } catch { /* sessionStorage blockiert */ }
}

export function clearPaketpreisDraft() {
    try { sessionStorage.removeItem(PAKETPREIS_DRAFT_KEY); } catch { /* ignore */ }
}

const KATEGORIEN = ['Damen', 'Herren', 'Farbe', 'Strähnen', 'Kosmetik', 'Dauerwelle', 'Spezial', 'Custom'];

const PaketpreisModal = ({
    draft,
    onDraftChange,
    onClose,
    onDiscard,
    onSave,
    current,
    formatCurrency,
}: {
    draft: PaketpreisDraft;
    onDraftChange: React.Dispatch<React.SetStateAction<PaketpreisDraft>>;
    onClose: () => void;
    onDiscard: () => void;
    onSave: (payload: PaketpreisSavePayload) => void;
    current: PaketpreisCurrentLeistung;
    formatCurrency: (value: number, digits?: number) => string;
}) => {
    const paketpreis = Number(String(draft.paketpreisRaw).replace(',', '.'));
    const hatEigenenPreis = String(draft.paketpreisRaw).trim() !== '' && Number.isFinite(paketpreis) && paketpreis > 0;
    const services = draft.services;

    const summen = useMemo(() => {
        const dauer = services.reduce((sum, s) => sum + (Number(s.dauerMinuten) || 0), 0);
        const einzel = services.reduce((sum, s) => sum + (Number(s.einzelpreisBrutto) || 0), 0);
        const material = services.reduce((sum, s) => sum + (Number(s.materialKostenNetto) || 0), 0);
        const geraete = services.reduce((sum, s) => sum + (Number(s.geraeteKostenNetto) || 0), 0);
        const effektiv = hatEigenenPreis ? paketpreis : einzel;
        const vorteil = einzel - effektiv;
        return { dauer, einzel, material, geraete, vorteil, effektiv };
    }, [services, paketpreis, hatEigenenPreis]);

    const gemerkterTitel = loadLetzterPaketTitel();
    const patch = (partial: Partial<PaketpreisDraft>) => {
        onDraftChange((prev) => {
            const next = { ...prev, ...partial };
            if (partial.titel !== undefined) rememberPaketTitel(next.titel);
            return next;
        });
    };

    const stepPaketpreis = (dir: -1 | 1) => {
        const base = hatEigenenPreis ? paketpreis : summen.einzel;
        const start = Number.isFinite(base) ? base : 0;
        const step = 5;
        const rem = ((start % step) + step) % step;
        const aligned = rem < 0.01 || step - rem < 0.01;
        let next = !aligned
            ? (dir > 0 ? Math.ceil(start / step) * step : Math.floor(start / step) * step)
            : Math.round((start + dir * step) / step) * step;
        if (next < 0) next = 0;
        patch({ paketpreisRaw: String(next) });
    };

    const addCurrent = () => {
        const typed = draft.naechsterName.trim();
        const base = typed || (current.name || '').trim() || 'Aktuelle Kalkulation';
        const n = services.length + 1;
        patch({
            naechsterName: '',
            services: [
                ...services,
                {
                    id: `ps_${Date.now()}_${n}`,
                    name: !typed && n > 1 ? `${base} ${n}` : base,
                    dauerMinuten: Number(current.dauerMinuten) || 0,
                    einzelpreisBrutto: Number(current.einzelpreisBrutto) || 0,
                    materialKostenNetto: Number(current.materialKostenNetto) || 0,
                    materialMenge: Number(current.materialMenge) || 0,
                    geraeteKostenNetto: Number(current.geraeteKostenNetto) || 0,
                },
            ],
        });
    };

    const handleSave = () => {
        if (services.length < 1) {
            alert('Bitte mindestens eine Leistung ins Paket übernehmen.');
            return;
        }
        if (!(summen.effektiv > 0)) {
            alert('Die übernommenen Leistungen haben noch keinen Preis.');
            return;
        }
        if (!draft.titel.trim()) {
            alert('Bitte einen Paket-Titel vergeben.');
            return;
        }
        const finalKat = draft.kategorie === 'Custom' ? draft.customKat.trim() : draft.kategorie;
        if (!finalKat) {
            alert('Bitte eine Kategorie angeben.');
            return;
        }
        const dauer = summen.dauer;
        onSave({
            name: draft.titel.trim(),
            kategorie: finalKat,
            bedienzeit: dauer,
            materialEinsatz: summen.material,
            materialMenge: services.reduce((sum, s) => sum + (Number(s.materialMenge) || 0), 0),
            geräteEinsatz: summen.geraete,
            finalerPreis: summen.effektiv,
            preisProStunde: dauer > 0 ? (summen.effektiv / dauer) * 60 : 0,
            isPackage: true,
            packageServices: services,
            gesamtDauerMinuten: dauer,
        });
    };

    const vorteilLabel = services.length === 0
        ? null
        : !hatEigenenPreis
          ? 'Paketpreis ist die Summe der Einzelpreise'
          : summen.vorteil > 0.004
            ? `Preisvorteil ${formatCurrency(summen.vorteil)}`
            : summen.vorteil < -0.004
              ? `Aufpreis ${formatCurrency(Math.abs(summen.vorteil))}`
              : 'Paketpreis entspricht der Einzelpreis-Summe';

    const hasDraft =
        services.length > 0 ||
        !!draft.titel.trim() ||
        !!draft.paketpreisRaw ||
        !!draft.naechsterName.trim() ||
        !!draft.customKat.trim();

    const handleAbort = () => {
        if (hasDraft) {
            const ok = window.confirm(
                'Paketpreis-Entwurf wirklich verwerfen und neu starten?\n\nAlle Teilleistungen und Eingaben in diesem Entwurf gehen verloren.'
            );
            if (!ok) return;
        }
        onDiscard();
    };

    const node = (
        <div className="fixed inset-0 z-[2147483647] bg-stone-900/60 dark:bg-black/80 backdrop-blur-sm overflow-y-auto">
            <div className="min-h-full flex items-start justify-center px-4 pt-6 pb-10 sm:pt-10">
                <div className="bg-pe-card rounded-2xl shadow-2xl border border-pe-border dark:border-[#222] w-full max-w-lg flex flex-col max-h-[calc(100vh-3rem)]">
                    <div className="px-6 sm:px-8 pt-6 pb-3 border-b border-pe-border dark:border-[#222] shrink-0">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <h2 className="text-2xl font-bold text-customBeige">Paketpreis erstellen</h2>
                                <p className="text-xs text-pe-muted dark:text-gray-400 mt-2">
                                    Entwurf bleibt gespeichert, wenn du zum Kalkulator zurückgehst und die nächste Leistung rechnest.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={handleAbort}
                                className="shrink-0 w-10 h-10 rounded-full border border-pe-border dark:border-[#333] text-pe-muted hover:text-red-400 hover:border-red-400/50 hover:bg-red-500/10 transition-colors flex items-center justify-center text-xl font-bold"
                                title="Abbrechen / Entwurf verwerfen"
                                aria-label="Abbrechen"
                            >
                                ×
                            </button>
                        </div>
                    </div>

                    <div className="px-6 sm:px-8 py-4 space-y-4 overflow-y-auto">
                        <div>
                            <label className="text-sm text-pe-muted dark:text-gray-400 font-semibold">Paket-Titel</label>
                            <input
                                type="text"
                                value={draft.titel}
                                onChange={(e) => patch({ titel: e.target.value })}
                                placeholder="Name des Pakets"
                                className="modal-input"
                                autoFocus
                            />
                            {gemerkterTitel && (
                                <label className="mt-2 flex items-center gap-2 text-sm text-pe-text cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={draft.titel.trim() === gemerkterTitel}
                                        onChange={(e) => patch({ titel: e.target.checked ? gemerkterTitel : '' })}
                                    />
                                    <span>{gemerkterTitel}</span>
                                </label>
                            )}
                        </div>

                        <div>
                            <label className="text-sm text-pe-muted dark:text-gray-400 font-semibold">Kategorie</label>
                            <select value={draft.kategorie} onChange={(e) => patch({ kategorie: e.target.value })} className="modal-input custom-select w-full mt-2">
                                {KATEGORIEN.map((k) => (
                                    <option key={k} value={k}>{k === 'Custom' ? 'Eigene Kategorie...' : k}</option>
                                ))}
                            </select>
                        </div>

                        {draft.kategorie === 'Custom' && (
                            <div>
                                <label className="text-sm text-pe-muted dark:text-gray-400 font-semibold">Eigene Kategorie</label>
                                <input
                                    type="text"
                                    value={draft.customKat}
                                    onChange={(e) => patch({ customKat: e.target.value })}
                                    placeholder="Name der Kategorie"
                                    className="modal-input"
                                />
                            </div>
                        )}

                        <div>
                            <label className="text-sm text-pe-muted dark:text-gray-400 font-semibold">Name dieser Teilleistung</label>
                            <input
                                type="text"
                                value={draft.naechsterName}
                                onChange={(e) => patch({ naechsterName: e.target.value })}
                                placeholder="z.B. Oberkopf Strähnen"
                                className="modal-input"
                            />
                        </div>

                        <button
                            type="button"
                            onClick={addCurrent}
                            className="w-full rounded-xl font-bold py-3 uppercase tracking-widest text-xs border border-[#E6C299] text-[#E6C299] bg-transparent hover:bg-white/5"
                        >
                            Aktuelle Leistung zufügen
                        </button>
                        <p className="text-xs text-pe-muted dark:text-gray-400">
                            Gerade im Kalkulator: {Number(current.dauerMinuten) || 0} Min. · {formatCurrency(Number(current.einzelpreisBrutto) || 0)}
                        </p>

                        <div className="border border-pe-border dark:border-[#222] rounded-xl overflow-hidden">
                            {services.length === 0 ? (
                                <p className="p-4 text-sm text-pe-muted dark:text-gray-400">Noch keine Teilleistung im Paket.</p>
                            ) : (
                                <ul>
                                    {services.map((s) => (
                                        <li key={s.id} className="flex items-start justify-between gap-3 p-3 border-b border-pe-border dark:border-[#222] last:border-0">
                                            <div className="min-w-0 flex-1">
                                                <input
                                                    type="text"
                                                    value={s.name}
                                                    onChange={(e) => patch({
                                                        services: services.map((row) => row.id === s.id ? { ...row, name: e.target.value } : row),
                                                    })}
                                                    className="modal-input !mt-0"
                                                />
                                                <div className="text-xs text-pe-muted dark:text-gray-400 font-numeric mt-1">
                                                    {s.dauerMinuten} Min. · {formatCurrency(s.einzelpreisBrutto)}
                                                    {(Number(s.materialMenge) || 0) > 0 ? ` · ${s.materialMenge} g` : ''}
                                                    {(Number(s.materialKostenNetto) || 0) > 0 ? ` · Mat. ${formatCurrency(s.materialKostenNetto)}` : ''}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => patch({ services: services.filter((x) => x.id !== s.id) })}
                                                className="text-red-500 font-bold px-2"
                                                title="Entfernen"
                                            >
                                                ✕
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div className="bg-pe-tile-header rounded-xl p-4 border border-pe-border dark:border-[#222] text-sm space-y-1">
                            <div className="flex justify-between">
                                <span className="text-pe-muted dark:text-gray-400">Gesamt-Bedienzeit</span>
                                <span className="font-numeric font-bold text-customBeige">{summen.dauer} Min.</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-pe-muted dark:text-gray-400">Einzelpreis-Summe</span>
                                <span className="font-numeric">{formatCurrency(summen.einzel)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-pe-muted dark:text-gray-400">Paketpreis</span>
                                <span className="font-numeric font-bold text-customBeige">{services.length ? formatCurrency(summen.effektiv) : '—'}</span>
                            </div>
                            {vorteilLabel && (
                                <div className="pt-2 text-customBeige font-semibold">{vorteilLabel}</div>
                            )}
                            <div className="pt-3">
                                <label className="text-xs text-pe-muted dark:text-gray-400 font-semibold">Eigener Paketpreis, nur bei Rabatt</label>
                                <div className="mt-2 flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="step-btn !w-12 !min-w-12 !h-12 !min-h-12 !text-2xl"
                                        aria-label="Paketpreis um 5 Euro senken"
                                        onClick={() => stepPaketpreis(-1)}
                                    >
                                        −
                                    </button>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        value={draft.paketpreisRaw}
                                        onChange={(e) => patch({ paketpreisRaw: e.target.value.replace(/[^\d.,]/g, '') })}
                                        placeholder={summen.einzel > 0 ? String(Math.round(summen.einzel * 100) / 100) : 'Summe'}
                                        className="modal-input !mt-0 flex-1 text-center font-numeric"
                                    />
                                    <button
                                        type="button"
                                        className="step-btn !w-12 !min-w-12 !h-12 !min-h-12 !text-2xl"
                                        aria-label="Paketpreis um 5 Euro erhöhen"
                                        onClick={() => stepPaketpreis(1)}
                                    >
                                        +
                                    </button>
                                </div>
                                <p className="text-xs text-pe-muted dark:text-gray-500 mt-1">Minus und Plus runden in 5-Euro-Schritten. Leer lassen übernimmt die Summe.</p>
                            </div>
                        </div>
                    </div>

                    <div className="px-6 sm:px-8 py-4 border-t border-pe-border dark:border-[#222] shrink-0 space-y-3 bg-pe-card rounded-b-2xl">
                        <div className="flex gap-3">
                            <button type="button" onClick={onClose} className="w-1/2 action-btn !bg-transparent hover:!bg-[#222]">
                                Weiter kalkulieren
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                className="w-1/2 action-btn !text-black !font-bold hover:!bg-white"
                                style={{ background: '#E6C299' }}
                            >
                                Zur Preisliste
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={handleAbort}
                            className="w-full action-btn !bg-transparent !border !border-red-500/40 !text-red-400 hover:!bg-red-500/10 hover:!text-red-300 uppercase tracking-widest text-[11px] font-black"
                        >
                            Abbrechen · Entwurf verwerfen
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(node, document.body);
};

export default PaketpreisModal;
